import { Participant as ChatParticipant } from '@twilio/conversations';
import { atom } from 'jotai';
import { atomFamily } from 'jotai/utils';
import { identity, pipe } from 'remeda';

import { MeasureBundleObject, NudgeObject, TreatmentPlanStatus } from '@grow-therapy-team/seedling-components';

import { AudioRecorder } from '../classes';
import { FormDataGroup } from '../components/forms/types';
import type { MeasureResponsesEntryMethod } from '../segment/types';
import {
  ParticipantIdsByConversationSid,
  chatParticipantsByIdAtom,
  conversationsBySidAtom,
  currentConversationSidAtom,
  participantIdsByConversationSidAtom,
  unreadMessageCountByConversationSidAtom,
} from '../session/state';
import { Provider, UserType, VisitorPresence, VisitorPresenceMap } from '../types';
import { createDerivedWritableAtom } from '../utils';
import { parseIdentity, participantsByIdAtom } from '../video-sdk';
import { participantUserIdsAtom } from '../video-sdk/core/room/participant/atoms';
import { twilioIdentityAtom } from '../video-sdk/twilio';
import { NoteTypeEnum } from './client-information/types';
import { ProviderDrawerState } from './drawerState';
import { scheduledPatientInformationAtom } from './schedule-preview/state';
import { getSelectedClientInfo } from './utils';

export enum ProviderState {
  IDLE = 'IDLE',
  COMPLETED = 'COMPLETED',
}

export enum ClientInformationDrawerTab {
  INFORMATION = 'information',
  TREATMENT = 'treatment',
  APPOINTMENTS = 'appointments',
}

export enum ParticipantStatus {
  IN_SESSION = 'IN SESSION',
  WAITING = 'WAITING',
  SCHEDULED = 'SCHEDULED',
}

export { ProviderDrawerState } from './drawerState';

export const PROVIDER_EDITING_FORM_OR_MEASURE_DRAWER_STATES: Readonly<Set<ProviderDrawerState>> = new Set([
  ProviderDrawerState.PENDING_MEASURES,
  ProviderDrawerState.CSSRS_FORM,
  ProviderDrawerState.PENDING_FORMS,
]);

export type ChatDrawerState = ProviderDrawerState.WAITING_ROOM_CHAT | ProviderDrawerState.SESSION_CHAT;

export const CHAT_DRAWER_STATES = new Set([ProviderDrawerState.WAITING_ROOM_CHAT, ProviderDrawerState.SESSION_CHAT]);

export type SelectedVisitor = Omit<VisitorPresence, 'timestamp'> & { isMinor?: boolean | null };

export type WaitingRoomConversationSidsByVisitorId = Record<string, string>;
export type PatientName = {
  patientFirstName?: string | null;
  patientLastName?: string | null;
};
export type FormDataByPatientShortId = Record<string, FormDataGroup>;
export type NewSessionConfirmationVariation = 'incompatible' | 'default';
export type ProviderAtom = {
  state: ProviderState;
  selectedPatientId?: string;
  selectedPatientIsEnrolledInStructuredCareProgram?: boolean;
  selectedClientUserIsUnderSafetyGuardAge: boolean;
  newSessionConfirmationData?: {
    visitorUuid: string;
    visitorName: string;
    patientShortId: string;
    variation: NewSessionConfirmationVariation;
  } | null;
  joiningVisitorIds: Set<string>;
  visitorPresenceMapTwilioToken?: string;
  waitingRoomConversationSidsByVisitorId: WaitingRoomConversationSidsByVisitorId;
  drawerState: ProviderDrawerState | null;
  sessionConversationSid?: string;
  userData?: Required<Provider>;
  inSessionPatientInformation?: SelectedVisitor;
  // Track if the provider is attempting to restart their session, to change how
  // we handle room/participant disconnects
  isRestartingSession: boolean;

  // selectedClientId represents the visitorId which is unique to a telehealth session
  selectedClientId?: string;
  // selectedClientUserShortId represents the user's clientShortId associated with their clientUser in the monolith
  selectedClientUserShortId?: string;

  formDataByPatientShortId: FormDataByPatientShortId;
  selectedClientMeasuresData: MeasureBundleObject[];
  selectedPatientPreferredName?: PatientName;
  selectedPatientNudges: NudgeObject[];
  selectedFormTitle?: string;
  activeTab: ClientInformationDrawerTab;
  audioRecordingEntities: {
    audioContext: AudioContext;
    audioDestinationNode: MediaStreamAudioDestinationNode;
    audioRecorder: AudioRecorder;
  } | null;
  selectedBundle?: MeasureBundleObject;
  whiteboardPersistenceKey?: string;
  selectedMeasureBundleItemId?: string;
  providerIsHelpModalOpen: boolean;
  patientShortIdWithFetchedMeasures?: string;
  patientShortIdToMeasuresFetchFailed: Record<string, boolean>;
  patientShortIdToHasCompletedMeasureResponses: Record<string, boolean>;
  visitorAdmissionTimes?: Record<string, string | undefined>;
};

export const DEFAULT_PROVIDER_ATOM_VALUE: ProviderAtom = {
  state: ProviderState.IDLE,
  joiningVisitorIds: new Set<string>(),
  waitingRoomConversationSidsByVisitorId: {},
  drawerState: null,
  formDataByPatientShortId: {},
  activeTab: ClientInformationDrawerTab.INFORMATION,
  audioRecordingEntities: null,
  selectedClientMeasuresData: [],
  selectedPatientNudges: [],
  providerIsHelpModalOpen: false,
  selectedClientUserIsUnderSafetyGuardAge: false,
  isRestartingSession: false,
  patientShortIdToMeasuresFetchFailed: {},
  patientShortIdToHasCompletedMeasureResponses: {},
};

export const treatmentPlanContentStatus = atom<TreatmentPlanStatus>(TreatmentPlanStatus.CURRENT_GOALS);

export const selectedVisitorNotesAtom = atom<{
  notes: string;
  patientId: number;
  noteType: NoteTypeEnum;
}>({ notes: '', patientId: 0, noteType: NoteTypeEnum.GROW_PROGRESS });

export const providerAtom = atom<ProviderAtom>(DEFAULT_PROVIDER_ATOM_VALUE);

export const selectedClientIdAtom = createDerivedWritableAtom(providerAtom, 'selectedClientId');
export const selectedPatientPreferredNameAtom = createDerivedWritableAtom(providerAtom, 'selectedPatientPreferredName');

export const selectedPatientNudgesAtom = createDerivedWritableAtom(providerAtom, 'selectedPatientNudges');

export const selectedClientUserShortIdAtom = createDerivedWritableAtom(providerAtom, 'selectedClientUserShortId');

export const selectedPatientId = createDerivedWritableAtom(providerAtom, 'selectedPatientId');
export const selectedClientMeasuresData = createDerivedWritableAtom(providerAtom, 'selectedClientMeasuresData');

// TODO CARED-2858 remove once we've fixed the unnecessary re-fetching of measures
export const patientShortIdWithFetchedMeasuresAtom = createDerivedWritableAtom(
  providerAtom,
  'patientShortIdWithFetchedMeasures',
);

export const patientShortIdToMeasuresFetchFailedAtom = createDerivedWritableAtom(
  providerAtom,
  'patientShortIdToMeasuresFetchFailed',
);

export const patientShortIdToHasCompletedMeasureResponsesAtom = createDerivedWritableAtom(
  providerAtom,
  'patientShortIdToHasCompletedMeasureResponses',
);

export const patientHasCompletedMeasureResponsesFamily = atomFamily((patientShortId: string) => {
  return atom((get) => {
    return get(patientShortIdToHasCompletedMeasureResponsesAtom)[patientShortId] ?? false;
  });
});

export const selectedBundleAtom = createDerivedWritableAtom(providerAtom, 'selectedBundle');

export const selectedMeasureBundleItemIdAtom = createDerivedWritableAtom(providerAtom, 'selectedMeasureBundleItemId');

export interface MeasureResponsesEntryContext {
  entryMethod: MeasureResponsesEntryMethod;
  patientShortId: string;
}

export const measureResponsesEntryContextAtom = atom<MeasureResponsesEntryContext | undefined>(undefined);

export const selectedClientInfoAtom = atom((get) => {
  const selectedClientId = get(selectedClientIdAtom);
  const waitingVisitors = get(waitingVisitorsAtom);
  const selectedPatientPreferredName = get(selectedPatientPreferredNameAtom);
  const inSessionParticipant = get(inSessionPatientInformationAtom);
  const selectedScheduledPatient = get(scheduledPatientInformationAtom);
  const prevSessionPatientInfo = get(prevSessionPatientInfoAtom);
  const participants = get(participantsByIdAtom);
  const selectedClientNudges = get(selectedPatientNudgesAtom);
  return getSelectedClientInfo(
    selectedClientId,
    waitingVisitors,
    participants,
    selectedPatientPreferredName,
    inSessionParticipant,
    selectedScheduledPatient,
    selectedClientNudges,
    prevSessionPatientInfo,
  );
});

export const selectedVisitorNotesAtomData = atom(
  (get) => get(selectedVisitorNotesAtom),
  (_get, set, notes: string, patientId: number, noteType: NoteTypeEnum) => {
    set(selectedVisitorNotesAtom, (prev) => ({
      ...prev,
      notes: notes,
      patientId,
      noteType,
    }));
  },
);

/**
 * Source of truth for the user data of the currently logged in provider. Should
 * only be mutated when the logged-in provider changes.
 */
export const userDataAtom = createDerivedWritableAtom(providerAtom, 'userData');
/**
 * Source of truth for the short ID of the currently logged in provider. If a
 * provider is logged in, this is expected to always be set. Some essential
 * flows (e.g. data track messages forwhiteboarding, fetching provider/patient
 * AI consent during admission, data track messages when a provider is
 * restarting their session, appointment rescheduling in the drawer) rely on
 * this atom, and mutating it should be done with extreme caution.
 */
export const providerShortIdAtom = atom((get) => {
  const userData = get(userDataAtom);
  return userData?.shortId;
});

export const providerStateAtom = createDerivedWritableAtom(providerAtom, 'state');

export const prevSessionPatientInfoAtom = atom<SelectedVisitor | undefined>(undefined);

export const drawerStateAtom = createDerivedWritableAtom(providerAtom, 'drawerState');

// TODO: Move this into the overarching provider atom; create derived atom instead
export const visitorPresenceMapSidAtom = atom<string | undefined>(undefined);

// TODO: Move this into the overarching provider atom; create derived atom instead
export const visitorPresenceAtom = atom<VisitorPresenceMap>({});

export const waitingVisitorsAtom = atom<VisitorPresenceMap>((get) =>
  Object.entries(get(visitorPresenceAtom))
    .filter(([visitorId]) => !get(participantsByIdAtom)[visitorId])
    .reduce((agg, [visitorId, visitor]) => ({ ...agg, [visitorId]: visitor }), {}),
);

export const numWaitingVisitorsAtom = atom((get) => Object.keys(get(waitingVisitorsAtom)).length);

export const pageAtom = atom<{ drawerIsOpen?: boolean }>({
  drawerIsOpen: false,
});

export const waitingRoomConversationSidsByVisitorIdAtom = createDerivedWritableAtom(
  providerAtom,
  'waitingRoomConversationSidsByVisitorId',
);

export const waitingRoomConversationSidsByVisitorIdFamily = atomFamily((visitorId: string) => {
  return atom((get) => get(waitingRoomConversationSidsByVisitorIdAtom)[visitorId]);
});

export const newSessionConfirmationDataAtom = createDerivedWritableAtom(providerAtom, 'newSessionConfirmationData');

export const joiningVisitorIdsAtom = createDerivedWritableAtom(providerAtom, 'joiningVisitorIds');

export const joiningVisitorIdFamily = atomFamily((visitorId: string) => {
  return atom((get) => get(joiningVisitorIdsAtom).has(visitorId));
});

export const visitorPresenceMapTwilioTokenAtom = createDerivedWritableAtom(
  providerAtom,
  'visitorPresenceMapTwilioToken',
);

export const totalUnreadWaitingRoomMessagesAtom = atom((get) =>
  Object.values(get(waitingRoomConversationSidsByVisitorIdAtom)).reduce((agg, conversationSid) => {
    const conversationUnreadCount = get(unreadMessageCountByConversationSidAtom)[conversationSid] ?? 0;
    return agg + conversationUnreadCount;
  }, 0),
);

export const totalUnreadMessageCountAtom = atom(
  (get) => get(sessionConversationUnreadMessageCount) + get(totalUnreadWaitingRoomMessagesAtom),
);

export const sessionConversationSidAtom = createDerivedWritableAtom(providerAtom, 'sessionConversationSid');

export const sessionConversationReadyAtom = atom((get) => {
  // check if the conversation exists
  const sessionConversationSid = get(sessionConversationSidAtom);
  if (!sessionConversationSid) {
    return false;
  }

  // check if the conversation has any expected participants
  const expectedParticipantIdentities = get(participantIdsByConversationSidAtom)[sessionConversationSid] ?? new Set();
  if (expectedParticipantIdentities.size === 0) {
    return false;
  }

  // otherwise ensure at least one expected conversation participant is in the room
  const sessionParticipantUserIds = new Set(get(participantUserIdsAtom));
  const expectedParticipantUserIds = new Set(
    Array.from(expectedParticipantIdentities).map((identity) => parseIdentity(identity).id),
  );
  const providerUserId = parseIdentity(get(twilioIdentityAtom)).id;
  return [...expectedParticipantUserIds]
    .filter((participantUserId) => participantUserId !== providerUserId)
    .some((participantUserId) => sessionParticipantUserIds.has(participantUserId));
});

/**
 * Stores the patient information of the patient that the provider is currently
 * in a meeting with.
 *
 * Set after a provider admits the patient and cleared on disconnect.
 */
export const inSessionPatientInformationAtom = createDerivedWritableAtom(providerAtom, 'inSessionPatientInformation');

/**
 * Track if the provider initiated restarting their session, to change how we
 * handle room/participant disconnects.
 *
 * Set when the provider ends a session with the intent to restart it and
 * re-admit their client(s).
 */
export const isRestartingSessionAtom = createDerivedWritableAtom(providerAtom, 'isRestartingSession');

export const sessionConversationAtom = atom((get) => {
  const conversationSid = get(sessionConversationSidAtom);
  if (!conversationSid) return;
  const conversationsBySid = get(conversationsBySidAtom);
  return conversationsBySid[conversationSid];
});

export const isChatDrawerOpenAtom = atom((get) => {
  const drawerState = get(providerAtom).drawerState;
  const currentConversationSid = get(currentConversationSidAtom);
  const waitingRoomConversationSidsByVisitorId = get(waitingRoomConversationSidsByVisitorIdAtom);
  const isCurrentWaitingRoomConversation = Object.values(waitingRoomConversationSidsByVisitorId).some(
    (sid) => sid === currentConversationSid,
  );

  switch (drawerState) {
    case ProviderDrawerState.WAITING_ROOM_CHAT:
      return currentConversationSid && isCurrentWaitingRoomConversation;
    case ProviderDrawerState.SESSION_CHAT:
      return true;
    default:
      return false;
  }
});

export const sessionConversationUnreadMessageCount = atom((get) => {
  const conversationSid = get(sessionConversationSidAtom);
  if (!conversationSid) return 0;
  return get(unreadMessageCountByConversationSidAtom)[conversationSid] ?? 0;
});

export const currentConversationPatientShortIdAtom = atom((get) => {
  const currentConversationSid = get(currentConversationSidAtom);
  if (!currentConversationSid) return undefined;
  const sessionConversationSid = get(sessionConversationSidAtom);
  if (currentConversationSid === sessionConversationSid) {
    return get(inSessionPatientInformationAtom)?.patientShortId;
  }
  // Waiting room: reverse-lookup conversationSid → visitorId → patientShortId
  const waitingRoomSidsByVisitorId = get(waitingRoomConversationSidsByVisitorIdAtom);
  const visitorId = Object.entries(waitingRoomSidsByVisitorId).find(([, sid]) => sid === currentConversationSid)?.[0];
  if (!visitorId) return undefined;
  return get(visitorPresenceAtom)[visitorId]?.patientShortId;
});

export const visitorChatParticipantsByConversationIdAtom = atom((get) => {
  return Object.entries(get(participantIdsByConversationSidAtom)).reduce(
    function participantIdsToParticipants(
      agg,
      [conversationSid, participantIds]: [
        keyof ParticipantIdsByConversationSid,
        ParticipantIdsByConversationSid[keyof ParticipantIdsByConversationSid],
      ],
    ) {
      return {
        ...agg,
        [conversationSid]: pipe(
          Array.from(participantIds),
          function filterClientParticipantIds(participantIds) {
            return participantIds.filter((participantId) => parseIdentity(participantId).userType === UserType.CLIENT);
          },
          function mapToParticipants(participantIds) {
            return participantIds.map((participantId) => get(chatParticipantsByIdAtom)[participantId]);
          },
          function filterValidParticipants(participants) {
            return participants.filter(identity);
          },
        ),
      };
    },
    {} as Record<string, ChatParticipant[]>,
  );
});

export const allVisitorChatParticipantsAtom = atom((get) => {
  // consider return Object.values(get(visitorChatParticipantsByConversationIdAtom)).flat();
  return Object.values(get(visitorChatParticipantsByConversationIdAtom)).reduce(
    (agg, participants) => agg.concat(participants),
    [],
  );
});

export const isInSessionWithSelectedClientAtom = atom((get) => {
  const selectedPatientShortId = get(selectedClientInfoAtom)?.patientShortId;
  const inSessionPatientShortId = get(inSessionPatientInformationAtom)?.patientShortId;

  if (!selectedPatientShortId || !inSessionPatientShortId) return false;

  return selectedPatientShortId === inSessionPatientShortId;
});

export const whiteboardPersistenceKeyAtom = createDerivedWritableAtom(providerAtom, 'whiteboardPersistenceKey');

export function checkIsEditingFormOrMeasure(drawerState: ProviderDrawerState) {
  return PROVIDER_EDITING_FORM_OR_MEASURE_DRAWER_STATES.has(drawerState);
}

export const isEditingFormOrMeasureAtom = atom((get) => {
  const drawerState = get(drawerStateAtom);
  return !!drawerState && checkIsEditingFormOrMeasure(drawerState);
});

export const providerIsHelpModalOpenAtom = createDerivedWritableAtom(providerAtom, 'providerIsHelpModalOpen');

export const visitorAdmissionTimesAtom = createDerivedWritableAtom(providerAtom, 'visitorAdmissionTimes');

export const waitingVisitorAdmissionTimesAtom = atom((get) => {
  const waitingVisitors = get(waitingVisitorsAtom);
  const visitorAdmissionTimes = get(visitorAdmissionTimesAtom);

  if (!visitorAdmissionTimes) {
    return {};
  }

  return Object.keys(waitingVisitors).reduce(
    (agg, visitorId) => {
      agg[visitorId] = visitorAdmissionTimes[visitorId];
      return agg;
    },
    {} as Record<string, string | undefined>,
  );
});
